import { ReadObject, SaveObject } from './storage'

export const DEVELOPMENT_MODE = false 
export const AKAMAI_ENDPOINT = 'https://gf-streaming.akamaized.net/'
export const API_ENDPOINT = 'https://api.tiqchat.com/api'
export const GetHeaders = async authenticated => {
	const headers = { 'Content-Type': 'application/json' }
	const authentication = await ReadObject('authentication')
	if (authenticated && authentication.jwt) {
		headers.Authorization = `Bearer ${authentication.jwt}`
	}
	return { headers }
}

export const ServerFetch = async (url, options, authenticated) => {
	const { headers } = await GetHeaders(authenticated)
	// console.info(url, options, headers)
	try{
		const response = await fetch(url, { ...options, headers }) 
		// CORREÇÃO: Usar response.status em vez de response.statusCode
		if (response.status === 403 && authenticated) {
			await SaveObject('authentication', {})
			// Opcional: redirecionar para login ou mostrar mensagem
			console.warn('Token expirado ou inválido. Limpando autenticação.')
		}
		try{
			return await response.json()
		}catch(err){
			console.log('ServerParseError', err)
			return response
		}  
	}catch(error){
		console.log('ServerFetchError', error)
		return false;
	}
}

export const GET = async (path, authenticated = false) => {
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			method: 'GET'
		},
		authenticated
	)
}

export const POST = async (path, body, authenticated = false) => {
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			method: 'POST',
			body: JSON.stringify(body)
		},
		authenticated
	)
}

export const PUT = async (path, body, authenticated = false) => {
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			method: 'PUT',
			body: JSON.stringify(body)
		},
		authenticated
	)
}

export const DELETE = async (path, authenticated = false) => {
	return await ServerFetch(
		`${API_ENDPOINT}${path}`,
		{
			method: 'DELETE'
		},
		authenticated
	)
} 

export const ReadAddressesByZipCode = async (zipCode) => {
    try{
        let result = await fetch(`https://viacep.com.br/ws/${ zipCode }/json/`);
        return result.json();
    }catch(err){ return false; }
}

// 🔍 FUNÇÃO DE DEBUG: Testar se outros endpoints autenticados funcionam
export const TestAuthenticatedEndpoint = async () => {
    console.log('🔍 Testando endpoint autenticado /me...');
    
    const authentication = await ReadObject('authentication');
    console.log('🔍 Token armazenado:', authentication?.jwt ? 'Presente' : 'Ausente');
    
    if (!authentication?.jwt) {
        console.log('🔍 Sem token JWT');
        return { error: 'No JWT token' };
    }
    
    try {
        const response = await GET('/me', true);
        console.log('🔍 Resposta do /me:', response);
        return response;
    } catch (error) {
        console.log('🔍 Erro ao testar /me:', error);
        return { error: error.message };
    }
};

// 🔍 FUNÇÃO DE TESTE: Verificar quais endpoints de upload existem
export const TestUploadEndpoints = async () => {
    console.log('🔍 Testando endpoints de upload disponíveis...');
    
    const authentication = await ReadObject('authentication');
    if (!authentication?.jwt) {
        return { error: 'Sem token JWT' };
    }
    
    const { headers } = await GetHeaders(true);
    const endpoints = [
        '/upload',           // Padrão Strapi - PRINCIPAL
        '/upload/files',     // Listagem de arquivos Strapi
        '/profile-pictures', // Endpoint customizado
        '/media',           // Alternativa comum
        '/files'            // Alternativa simples
    ];
    
    const results = {};
    
    for (const endpoint of endpoints) {
        try {
            console.log(`🔍 Testando GET ${API_ENDPOINT}${endpoint}...`);
            const response = await fetch(`${API_ENDPOINT}${endpoint}`, {
                method: 'GET',
                headers
            });
            
            results[endpoint] = {
                status: response.status,
                exists: response.status !== 404,
                accessible: response.status < 400 || response.status === 403, // 403 = existe mas sem permissão
                headers: Object.fromEntries(response.headers.entries())
            };
            
            console.log(`🔍 ${endpoint}: Status ${response.status}`);
        } catch (error) {
            results[endpoint] = { error: error.message };
            console.log(`🔍 ${endpoint}: Erro ${error.message}`);
        }
    }
    
    console.log('🔍 Resultados dos testes:', results);
    return results;
};

// 🔍 FUNÇÃO ALTERNATIVA: Testar upload com endpoint padrão do Strapi
// Converte imagens HEIC/HEIF para JPEG quando necessário (iOS),
// e opcionalmente normaliza tipos/nomes para melhor compatibilidade no backend.
const loadHeic2Any = () => {
	return new Promise((resolve, reject) => {
		if(typeof window !== 'undefined' && window.heic2any){ return resolve(window.heic2any) }
		const script = document.createElement('script')
		script.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js'
		script.async = true
		script.onload = () => window.heic2any ? resolve(window.heic2any) : reject(new Error('heic2any não disponível'))
		script.onerror = () => reject(new Error('Falha ao carregar heic2any'))
		document.head.appendChild(script)
	})
}

const ensureWebCompatibleImage = async (originalFile) => {
	try{
		const isHeic = /\.(heic|heif)$/i.test(originalFile.name || '') || /image\/(heic|heif)/i.test(originalFile.type || '')

		// Compressão preventiva para arquivos grandes (ex.: fotos de celular)
		const MAX_PRE_COMPRESS_BYTES = Math.floor(1.5 * 1024 * 1024) // ~1.5MB
		if(!isHeic && (originalFile.size || 0) <= MAX_PRE_COMPRESS_BYTES){
			return originalFile
		}

 		// HEIC/HEIF: usar heic2any quando disponível
		if(isHeic){
			try{
				const heic2any = await loadHeic2Any()
				const converted = await heic2any({ blob: originalFile, toType: 'image/jpeg', quality: 0.8 })
				const convertedBlob = Array.isArray(converted) ? converted[0] : converted
				const safeName = (originalFile.name || 'image').replace(/\.(heic|heif)$/i, '.jpg')
				return new File([convertedBlob], safeName, { type: 'image/jpeg' })
			}catch(err){
				console.warn('ensureWebCompatibleImage: heic2any falhou, tentando via canvas', err)
			}
		}


		// Utilitários para orientação EXIF
		const readArrayBuffer = (file) => new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.onload = () => resolve(reader.result)
			reader.onerror = reject
			reader.readAsArrayBuffer(file)
		})

		const getExifOrientation = (arrayBuffer) => {
			try{
				const view = new DataView(arrayBuffer)
				if (view.getUint16(0, false) !== 0xFFD8) return 1 // not JPEG
				let offset = 2
				const length = view.byteLength
				while (offset < length) {
					const marker = view.getUint16(offset, false)
					offset += 2
					if (marker === 0xFFE1) { // APP1
						const exifLength = view.getUint16(offset, false)
						offset += 2
						if (view.getUint32(offset, false) !== 0x45786966) return 1 // 'Exif'
						offset += 6
						const little = view.getUint16(offset, false) === 0x4949
						offset += view.getUint32(offset + 4, little)
						const tags = view.getUint16(offset, little)
						offset += 2
						for (let i = 0; i < tags; i++) {
							const tagOffset = offset + (i * 12)
							const tag = view.getUint16(tagOffset, little)
							if (tag === 0x0112) { // Orientation
								return view.getUint16(tagOffset + 8, little)
							}
						}
						break
					} else if ((marker & 0xFF00) !== 0xFF00) {
						break
					} else {
						offset += view.getUint16(offset, false)
					}
				}
			}catch(err){ /* ignore */ }
			return 1
		}

		// Ler como DataURL (para desenhar) e ArrayBuffer (para orientação) em paralelo
		const [dataUrl, arrayBuffer] = await Promise.all([
			new Promise((resolve, reject) => {
				const reader = new FileReader()
				reader.onload = () => resolve(reader.result)
				reader.onerror = reject
				reader.readAsDataURL(originalFile)
			}),
			readArrayBuffer(originalFile)
		])

		// Criar imagem e desenhar em canvas para gerar JPEG/compactar
		const imageElement = await new Promise((resolve, reject) => {
			const img = new Image()
			img.onload = () => resolve(img)
			img.onerror = reject
			img.src = dataUrl
		})

		const orientation = getExifOrientation(arrayBuffer)
		const canvas = document.createElement('canvas')
		// Redimensiona mantendo proporção para um máximo de 1280px no maior lado
		const MAX_DIMENSION = 512
		const srcWidth = imageElement.width
		const srcHeight = imageElement.height
		const baseScale = Math.min(1, MAX_DIMENSION / Math.max(srcWidth, srcHeight))
		let targetWidth = Math.max(1, Math.round(srcWidth * baseScale))
		let targetHeight = Math.max(1, Math.round(srcHeight * baseScale))

		// Para orientações 5,6,7,8 o width/height invertem
		const swapSides = [5,6,7,8].includes(orientation)
		canvas.width = swapSides ? targetHeight : targetWidth
		canvas.height = swapSides ? targetWidth : targetHeight
		const ctx = canvas.getContext('2d')

		// Aplicar transformações de orientação
		switch(orientation){
			case 2:
				ctx.translate(canvas.width, 0)
				ctx.scale(-1, 1)
				break
			case 3:
				ctx.translate(canvas.width, canvas.height)
				ctx.rotate(Math.PI)
				break
			case 4:
				ctx.translate(0, canvas.height)
				ctx.scale(1, -1)
				break
			case 5:
				ctx.rotate(0.5 * Math.PI)
				ctx.scale(1, -1)
				break
			case 6:
				ctx.rotate(0.5 * Math.PI)
				ctx.translate(0, -canvas.width)
				break
			case 7:
				ctx.rotate(0.5 * Math.PI)
				ctx.translate(canvas.height, -canvas.width)
				ctx.scale(-1, 1)
				break
			case 8:
				ctx.rotate(-0.5 * Math.PI)
				ctx.translate(-canvas.height, 0)
				break
			default:
				break
		}

		// Desenhar imagem redimensionada
		ctx.drawImage(imageElement, 0, 0, targetWidth, targetHeight)

		// Normalizar para avatar quadrado, com crop central e tamanho final reduzido
		const cropSide = Math.min(canvas.width, canvas.height)
		const cropX = Math.max(0, Math.floor((canvas.width - cropSide) / 2))
		const cropY = Math.max(0, Math.floor((canvas.height - cropSide) / 2))

		const AVATAR_TARGET = 512 // tamanho final (px) do avatar padronizado
		const squareCanvas = document.createElement('canvas')
		squareCanvas.width = AVATAR_TARGET
		squareCanvas.height = AVATAR_TARGET
		const squareCtx = squareCanvas.getContext('2d')
		squareCtx.drawImage(
			canvas,
			cropX, cropY, cropSide, cropSide,
			0, 0, AVATAR_TARGET, AVATAR_TARGET
		)

		const blob = await new Promise((resolve, reject) => {
			squareCanvas.toBlob((b) => b ? resolve(b) : reject(new Error('Falha ao converter imagem')), 'image/jpeg', 0.82)
		})

		const safeName = (originalFile.name || 'image').replace(/\.(heic|heif)$/i, '.jpg')
		return new File([blob], safeName, { type: 'image/jpeg' })
	}catch(err){
		console.warn('ensureWebCompatibleImage: conversão falhou, usando arquivo original', err)
		return originalFile
	}
}

export const PostImageAlternative = async (fileToUpload) => {
	const prepared = await ensureWebCompatibleImage(fileToUpload)
    const formData = new FormData()
	formData.append('files', prepared, prepared.name)  // Strapi usa 'files' por padrão
    
    // Obtenha o token de autenticação atual
    const authentication = await ReadObject('authentication');
    if (!authentication || !authentication.jwt) {
        console.error('Token de autenticação não encontrado');
        throw new Error('Usuário não autenticado');
    }
    
    let { headers } = await GetHeaders(true) 
    delete headers['Content-Type'] // Necessário para upload de arquivos
    
    console.log('🔍 [ALT] Headers sendo enviados:', headers); 
    console.log('🔍 [ALT] URL do upload:', `${API_ENDPOINT}/upload`);
	console.log('🔍 [ALT] Arquivo para upload:', {
		name: prepared.name,
		size: prepared.size,
		type: prepared.type
	});
    
    let response = await fetch(`${API_ENDPOINT}/upload`, { 
        method: 'POST', 
        body: formData, 
        headers
    });  
    
    console.log('🔍 [ALT] Status da resposta:', response.status);
    console.log('🔍 [ALT] Headers da resposta:', Object.fromEntries(response.headers.entries()));
    
    // Tratamento específico para erro 403 (Forbidden)
    if (response.status === 403) {
        // Tentar ler a resposta para entender o erro
        let errorDetail = '';
        try {
            const errorResponse = await response.clone().text();
            errorDetail = errorResponse;
            console.log('🔍 [ALT] Detalhes do erro 403:', errorDetail);
        } catch (e) {
            console.log('🔍 [ALT] Não foi possível ler detalhes do erro 403');
        }
        
        throw new Error(`Erro 403 - Acesso negado: ${errorDetail}`);
    }
    
    if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 [ALT] Erro no upload:', response.status, errorText);
        throw new Error(`Erro no upload: ${response.status} - ${errorText}`);
    }
    
    let responseJson = await response.json()  
    console.log('🔍 [ALT] Resposta do upload:', responseJson);
    return responseJson; 
};

export const PostImage = async (fileToUpload) => {
	const prepared = await ensureWebCompatibleImage(fileToUpload)
    const formData = new FormData()
	formData.append('file', prepared, prepared.name)  
    
    // Obtenha o token de autenticação atual
    const authentication = await ReadObject('authentication');
    if (!authentication || !authentication.jwt) {
        console.error('Token de autenticação não encontrado');
        throw new Error('Usuário não autenticado');
    }
    
    let { headers } = await GetHeaders(true) 
    delete headers['Content-Type'] // Necessário para upload de arquivos
    
    console.log('🔍 Headers sendo enviados:', headers); 
    console.log('🔍 URL do upload:', `${API_ENDPOINT}/profile-pictures`);
	console.log('🔍 Arquivo para upload:', {
		name: prepared.name,
		size: prepared.size,
		type: prepared.type
	});
    
    let response = await fetch(`${API_ENDPOINT}/profile-pictures`, { 
        method: 'POST', 
        body: formData, 
        headers
    });  
    
    console.log('🔍 Status da resposta:', response.status);
    console.log('🔍 Headers da resposta:', Object.fromEntries(response.headers.entries()));
    
    // Tratamento específico para erro 403 (Forbidden)
    if (response.status === 403) {
        // Tentar ler a resposta para entender o erro
        let errorDetail = '';
        try {
            const errorResponse = await response.clone().text();
            errorDetail = errorResponse;
            console.log('🔍 Detalhes do erro 403:', errorDetail);
        } catch (e) {
            console.log('🔍 Não foi possível ler detalhes do erro 403');
        }
        
        // NÃO limpar autenticação - é problema específico do endpoint
        console.warn('Erro 403 no endpoint /profile-pictures - pode não existir ou ter permissões diferentes');
        throw new Error(`Erro 403 no endpoint /profile-pictures: ${errorDetail}`);
    }
    
    if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Erro no upload:', response.status, errorText);
        throw new Error(`Erro no upload: ${response.status} - ${errorText}`);
    }
    
    let responseJson = await response.json()  
    console.log('🔍 Resposta do upload:', responseJson);
    return responseJson; 
}
  
export const PrepareImageFile = (image) => {
	let btc = window.atob(image.source)
	let btn = new Array(btc.length);
	for (var i = 0; i < btc.length; i++) {
		btn[i] = btc.charCodeAt(i);
	}
	var bta = new Uint8Array(btn);
	let boobs = new Blob([bta], {type: 'image/png' } );
	return new File([boobs], `${ image.filename }`)
} 

export const UploadImage = async (file) => {
    // Envia diretamente o File via FormData utilizando o endpoint alternativo
    // Mantém a semântica de "fallback" para /profile-pictures ao ser usada pelos chamadores
    return await PostImage(file)
} 
 
export const parseStrapiImage = url => {
    return !url ? '' : url.indexOf('://') !== -1 ? url : `${API_ENDPOINT.replace('/api', '')}${url}`
}